import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { getAppIcon, getAppColor, formatDate, formatCurrency, getLicenseStatusColor, isLicenseExpired } from "@/lib/utils";
import { License, Application, Employee, Team, PaymentCard, LicensePaymentCard } from "@shared/schema";
import {
  Edit,
  Users,
  CreditCard,
  Calendar,
  Info,
  User,
  Building2,
  CreditCardIcon,
  CloudUpload,
  Upload,
  PlusCircle,
  Pencil,
  Trash2,
  FileText as FileTextIcon,
  CalendarIcon,
  X,
  UserPlus,
  ChevronsUpDown,
  Check
} from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import AddHistoricalRenewalDialog from "./add-historical-renewal-dialog";
import EditHistoricalRenewalDialog from "./edit-historical-renewal-dialog";
import ViewInvoiceDialog from "./view-invoice-dialog";
import DeleteRenewalDialog from "./delete-renewal-dialog";

type ViewLicenseDialogProps = {
  licenseId: number | null;
  isOpen: boolean;
  onClose: () => void;
  // Jumps straight into the Add Historical Renewal flow once the license
  // loads -- used right after creating a backdated license, so recording
  // its renewal history is the very next step instead of a separate trip.
  autoOpenHistoricalRenewal?: boolean;
};

export default function ViewLicenseDialog({
  licenseId,
  isOpen,
  onClose,
  autoOpenHistoricalRenewal,
}: ViewLicenseDialogProps) {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [showAddHistoricalDialog, setShowAddHistoricalDialog] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState<number | null>(null);
  const [editingRenewal, setEditingRenewal] = useState<number | null>(null);
  const [deletingRenewal, setDeletingRenewal] = useState<number | null>(null);
  const [invoiceUpdateCounter, setInvoiceUpdateCounter] = useState<number>(0);
  const [selectedRenewalDate, setSelectedRenewalDate] = useState<Date | null>(null);
  const [assignPanelOpen, setAssignPanelOpen] = useState(false);
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  
  // State for historical renewals with proper tracking
  const [historicalRenewals, setHistoricalRenewals] = useState<{
    id: number;
    licenseId: number;
    renewalDate: Date | null;
    newCost: number;
    previousCost: number | null;
    currency: string;
    invoiceUrl: string | null;
    notes: string;
  }[]>([]);
  const { toast } = useToast();
  
  // Handler for file uploads
  const handleFileUpload = (files: File[]) => {
    // Filter for allowed file types and size limit (10MB)
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not a supported file type. Please upload PDF, PNG, or JPG files.`,
          variant: "destructive"
        });
        return false;
      }
      
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds the 10MB size limit.`,
          variant: "destructive"
        });
        return false;
      }
      
      return true;
    });
    
    setUploadedFiles((prev: File[]) => [...prev, ...validFiles]);
  };
  
  // Query the license data when the dialog is open and an ID is provided
  const { data: license, isLoading: licenseLoading } = useQuery<License>({
    queryKey: ['/api/licenses', licenseId],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      const res = await fetch(`/api/licenses/${licenseId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch license');
      }
      return res.json();
    },
    enabled: isOpen && licenseId !== null,
  });

  useEffect(() => {
    if (isOpen && autoOpenHistoricalRenewal && license) {
      setShowAddHistoricalDialog(true);
    }
  }, [isOpen, autoOpenHistoricalRenewal, license]);

  // Query historical renewals data
  const { data: fetchedRenewals, isLoading: renewalsLoading } = useQuery({
    queryKey: ['/api/licenses', licenseId, 'historical-renewals'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      console.log(`🔍 Frontend fetching historical renewals for license ${licenseId}`);
      const res = await fetch(`/api/licenses/${licenseId}/historical-renewals`);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error:', res.status, errorText);
        throw new Error('Failed to fetch historical renewals');
      }
      const data = await res.json();
      console.log(`✅ Frontend received historical renewals:`, data);
      return data;
    },
    enabled: isOpen && licenseId !== null,
  });
  
  // Update state when renewals data is loaded
  useEffect(() => {
    if (fetchedRenewals) {
      console.log('Raw fetched renewals from API:', fetchedRenewals);
      // Format dates from string to Date objects
      const formattedRenewals = fetchedRenewals.map((renewal: any) => ({
        ...renewal,
        renewalDate: new Date(renewal.renewalDate)
      }));
      console.log('Formatted renewals for state:', formattedRenewals);
      setHistoricalRenewals(formattedRenewals);
    }
  }, [fetchedRenewals]);

  // Query application data to display application details
  const { data: applications = [], isLoading: appsLoading } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
    enabled: isOpen && licenseId !== null,
  });
  
  // We're now only using employees and teams, not the legacy "users" concept
  
  // Query employees assigned to this license
  const { data: assignedEmployees = [], isLoading: employeesLoading } = useQuery<Employee[]>({
    queryKey: ['/api/licenses', licenseId, 'employees'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      try {
        const res = await fetch(`/api/licenses/${licenseId}/employees`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Error fetching license employees:", error);
        return [];
      }
    },
    enabled: isOpen && licenseId !== null,
  });
  
  // Query teams assigned to this license
  const { data: assignedTeams = [], isLoading: teamsLoading } = useQuery<Team[]>({
    queryKey: ['/api/licenses', licenseId, 'teams'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      try {
        const res = await fetch(`/api/licenses/${licenseId}/teams`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Error fetching license teams:", error);
        return [];
      }
    },
    enabled: isOpen && licenseId !== null,
  });
  
  // Query payment card associated with this license
  const { data: paymentCard, isLoading: cardLoading } = useQuery<PaymentCard | null>({
    queryKey: ['/api/licenses', licenseId, 'payment-card'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      try {
        const res = await fetch(`/api/licenses/${licenseId}/payment-card`);
        if (!res.ok) return null;
        return res.json();
      } catch (error) {
        console.error("Error fetching license payment card:", error);
        return null;
      }
    },
    enabled: isOpen && licenseId !== null,
  });

  const isLoading = licenseLoading || appsLoading || employeesLoading || teamsLoading || cardLoading;

  const queryClient = useQueryClient();

  const { data: allEmployees = [] } = useQuery<Employee[]>({
    queryKey: ['/api/employees'],
    enabled: isOpen && licenseId !== null && assignPanelOpen,
  });

  const assignMutation = useMutation({
    mutationFn: async (employeeId: number) => {
      const res = await fetch('/api/license-employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId, employeeId }),
      });
      if (!res.ok) throw new Error('Failed to assign employee');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId, 'employees'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      setSelectedEmployee(null);
      toast({ title: "Employee assigned successfully" });
    },
    onError: () => {
      toast({ title: "Failed to assign employee", variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (employeeId: number) => {
      const res = await fetch('/api/license-employees', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId, employeeId }),
      });
      if (!res.ok) throw new Error('Failed to remove employee');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId, 'employees'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      toast({ title: "Employee removed successfully" });
    },
    onError: () => {
      toast({ title: "Failed to remove employee", variant: "destructive" });
    },
  });
  
  // Find the associated application
  const application = license?.applicationId 
    ? applications.find(app => app.id === license.applicationId) 
    : undefined;

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>License Details</DialogTitle>
          <DialogDescription>
            View details for this license
          </DialogDescription>
        </DialogHeader>

        {isLoading || !license ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <div className="mt-4 space-y-6">
            <div className="flex items-start gap-4">
              <div className={`h-16 w-16 rounded ${getAppColor((application as any)?.logo || null)} flex items-center justify-center flex-shrink-0`}>
                <i className={`${getAppIcon((application as any)?.logo || null)} text-2xl`}></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-1">{license.name}</h3>
                <p className="text-muted-foreground">{application?.name || 'Unknown Application'} • {license.billingCycle}</p>
                <div className="flex gap-2 mt-2">
                  <Badge className={getLicenseStatusColor(
                    // Show "expired" status if the license has passed its renewal date
                    isLicenseExpired(license.renewalDate) && license.status !== 'discontinued' 
                      ? 'expired' 
                      : license.status
                  )}>
                    {isLicenseExpired(license.renewalDate) && license.status !== 'discontinued'
                      ? 'Expired'
                      : license.status && typeof license.status === 'string'
                        ? license.status.charAt(0).toUpperCase() + license.status.slice(1)
                        : 'Unknown'}
                  </Badge>
                </div>
              </div>
            </div>

            <Tabs defaultValue="details">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
                <TabsTrigger value="details" className="flex items-center justify-center">
                  <Info className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Details</span>
                  <span className="sm:hidden">Info</span>
                </TabsTrigger>
                <TabsTrigger value="employees" className="flex items-center justify-center">
                  <User className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Employees</span>
                  <span className="sm:hidden">Staff</span>
                </TabsTrigger>
                <TabsTrigger value="teams" className="flex items-center justify-center">
                  <Building2 className="h-4 w-4 mr-1 sm:mr-2" />
                  <span>Teams</span>
                </TabsTrigger>
                <TabsTrigger value="billing" className="flex items-center justify-center">
                  <CreditCard className="h-4 w-4 mr-1 sm:mr-2" />
                  <span>Billing</span>
                </TabsTrigger>
              </TabsList>
              
              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Start Date</h4>
                    <p>{formatDate(license.startDate || null)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Renewal Date</h4>
                    <p>{formatDate(license.renewalDate || null)}</p>
                    {license.renewalDate && (
                      <span className="text-xs text-muted-foreground">
                        {(() => {
                          const renewalDate = new Date(license.renewalDate);
                          const now = new Date();
                          const daysUntilRenewal = Math.floor((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                          if (daysUntilRenewal <= 0) return "Overdue";
                          if (daysUntilRenewal <= 30) return `${daysUntilRenewal} days remaining`;
                          return "";
                        })()}
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">License Status</h4>
                    <p>
                      {isLicenseExpired(license.renewalDate) && license.status !== 'discontinued'
                        ? 'Expired'
                        : license.status}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Vendor</h4>
                    <p>{(application as any)?.vendor || "N/A"}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Total Seats</h4>
                    <p>{license.totalSeats}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Assigned Seats</h4>
                    <p>{license.assignedSeats} ({Math.round((license.assignedSeats / license.totalSeats) * 100)}%)</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Product Owner</h4>
                    {(license as any).productOwnerName ? (
                      <div className="flex items-center gap-2">
                        <div className="bg-purple-100 text-purple-700 rounded-full h-6 w-6 flex items-center justify-center text-xs font-medium flex-shrink-0">
                          {(license as any).productOwnerName.charAt(0).toUpperCase()}
                        </div>
                        <span>{(license as any).productOwnerName}</span>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Not assigned</p>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              {/* Seat allocation moved to Employees & Teams tabs for clarity */}
              
              {/* Employees Tab */}
              <TabsContent value="employees" className="pt-4">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium">Assigned Employees</h4>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex items-center"
                      onClick={() => {
                        setAssignPanelOpen(!assignPanelOpen);
                        setSelectedEmployee(null);
                      }}
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      {assignPanelOpen ? 'Cancel' : 'Assign Employee'}
                    </Button>
                  </div>

                  {/* Inline assignment panel */}
                  {assignPanelOpen && (
                    <div className="mb-4 p-3 border rounded-md bg-slate-50 space-y-3">
                      <p className="text-xs font-medium text-slate-600">
                        Available seats: {license.totalSeats - license.assignedSeats} of {license.totalSeats}
                      </p>
                      <div className="flex gap-2">
                        <Popover open={employeeSearchOpen} onOpenChange={setEmployeeSearchOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" className="flex-1 justify-between text-sm font-normal">
                              {selectedEmployee ? selectedEmployee.fullName : 'Search employees...'}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0">
                            <Command>
                              <CommandInput placeholder="Search by name or email..." />
                              <CommandList>
                                <CommandEmpty>No employees found.</CommandEmpty>
                                <CommandGroup>
                                  {allEmployees
                                    .filter(emp => !assignedEmployees.some(ae => ae.id === emp.id))
                                    .map(emp => (
                                      <CommandItem
                                        key={emp.id}
                                        value={`${emp.fullName} ${emp.email}`}
                                        onSelect={() => {
                                          setSelectedEmployee(emp);
                                          setEmployeeSearchOpen(false);
                                        }}
                                      >
                                        <Check className={`mr-2 h-4 w-4 ${selectedEmployee?.id === emp.id ? 'opacity-100' : 'opacity-0'}`} />
                                        <div className="flex flex-col">
                                          <span className="font-medium">{emp.fullName}</span>
                                          <span className="text-xs text-muted-foreground">{emp.email}</span>
                                        </div>
                                      </CommandItem>
                                    ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <Button
                          size="sm"
                          disabled={!selectedEmployee || assignMutation.isPending || license.assignedSeats >= license.totalSeats}
                          onClick={() => {
                            if (selectedEmployee) assignMutation.mutate(selectedEmployee.id);
                          }}
                        >
                          Assign
                        </Button>
                      </div>
                      {license.assignedSeats >= license.totalSeats && (
                        <p className="text-xs text-red-500">No available seats. Upgrade your license to assign more employees.</p>
                      )}
                    </div>
                  )}

                  {employeesLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  ) : assignedEmployees.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {assignedEmployees.map((employee) => (
                        <div key={employee.id} className="flex items-center p-2 bg-slate-50 rounded text-sm">
                          <div className="bg-slate-200 rounded-full h-6 w-6 flex items-center justify-center mr-2 flex-shrink-0">
                            {employee.fullName ? employee.fullName.charAt(0).toUpperCase() : 'E'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{employee.fullName}</div>
                            <div className="text-xs text-muted-foreground flex flex-col sm:flex-row sm:items-center">
                              <span className="truncate">{employee.email}</span>
                              {employee.jobTitle && (
                                <span className="bg-slate-200 rounded-full px-2 py-0.5 text-xs mt-1 sm:mt-0 sm:ml-2 inline-block sm:inline w-fit">
                                  {employee.jobTitle}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 ml-1 shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            disabled={removeMutation.isPending}
                            onClick={() => removeMutation.mutate(employee.id)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center p-4 border rounded-md bg-slate-50">
                      <p className="text-sm text-muted-foreground">No employees assigned to this license.</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => setAssignPanelOpen(true)}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Assign Employees
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Teams Tab */}
              <TabsContent value="teams" className="pt-4">
                <div>
                  <h4 className="text-sm font-medium mb-3">Assigned Teams</h4>
                  {teamsLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  ) : assignedTeams.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {assignedTeams.map((team) => (
                        <div key={team.id} className="flex items-center p-2 bg-slate-50 rounded text-sm">
                          <div className="bg-slate-200 rounded h-10 w-10 flex items-center justify-center mr-2 flex-shrink-0">
                            <Building2 className="h-5 w-5 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{team.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {team.description && (
                                <span className="truncate block">{team.description}</span>
                              )}
                              {/* Removed department field as it was causing a type error */}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center p-4 border rounded-md bg-slate-50">
                      <p className="text-sm text-muted-foreground">No teams assigned to this license.</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Billing Tab */}
              <TabsContent value="billing" className="pt-4">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium mb-1">Cost Details</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Cost per seat</p>
                        <p className="text-lg font-semibold">{formatCurrency(license.cost / license.totalSeats)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total cost</p>
                        <p className="text-lg font-semibold">{formatCurrency(license.cost)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Billing cycle</p>
                        <p>{license.billingCycle}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Currency</p>
                        <p>{license.currency || "USD"}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Card Details */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">Payment Method</h4>
                    {cardLoading ? (
                      <div className="space-y-2">
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                      </div>
                    ) : paymentCard ? (
                      <div className="p-4 bg-slate-50 rounded-md border">
                        <div className="flex items-center">
                          <div className="bg-slate-200 rounded h-10 w-10 flex items-center justify-center mr-3 flex-shrink-0">
                            <CreditCardIcon className="h-5 w-5 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{paymentCard.cardName}</p>
                            <div className="flex items-center text-sm text-muted-foreground">
                              <span>{paymentCard.cardType} •</span>
                              <span className="ml-1">••••{paymentCard.lastFourDigits}</span>
                              <span className="ml-2 px-2 py-0.5 bg-slate-200 rounded-full text-xs">
                                {paymentCard.cardProvider}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              <span>Expires: {paymentCard.expiryMonth}/{paymentCard.expiryYear}</span>
                              {paymentCard.isDefault && (
                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  Default
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-muted-foreground">
                          <p>Assigned on {formatDate(new Date())}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-4 border rounded-md bg-slate-50">
                        <p className="text-sm text-muted-foreground">No payment method assigned to this license.</p>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-1">Annual Cost Projection</h4>
                    <p className="text-lg font-semibold">
                      {formatCurrency(
                        license.billingCycle === "Monthly" 
                          ? license.cost * 12 
                          : license.billingCycle === "Quarterly" 
                            ? license.cost * 4 
                            : license.cost
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Based on current {license.billingCycle.toLowerCase()} rate
                    </p>
                  </div>
                  
                  {/* Historical Renewal Records Section */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-medium">Historical Renewals</h4>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex items-center"
                        onClick={() => {
                          setShowAddHistoricalDialog(true);
                        }}
                      >
                        <PlusCircle className="h-4 w-4 mr-1" />
                        Add Historical Renewal
                      </Button>
                    </div>
                    
                    <div className="border rounded-md overflow-hidden">
                      <div className="bg-slate-50 p-3 border-b">
                        <div className="grid grid-cols-4 gap-2 text-xs font-medium text-slate-500">
                          <div>Date</div>
                          <div>Amount</div>
                          <div>Invoice</div>
                          <div className="text-right">Actions</div>
                        </div>
                      </div>
                      
                      {/* Historical renewal records using state management */}
                      <div className="divide-y">
                        {historicalRenewals.map((renewal) => (
                          <div key={renewal.id} className="p-3 bg-white hover:bg-slate-50">
                            <div className="grid grid-cols-4 gap-2 text-sm">
                              <div className="flex items-center">
                                <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                                {formatDate(renewal.renewalDate)}
                              </div>
                              <div>{formatCurrency(renewal.newCost)}</div>
                              <div>
                                {renewal.invoiceUrl ? (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-7 text-xs flex items-center text-blue-600"
                                    onClick={() => {
                                      // Debug: Log renewal data
                                      console.log('Clicking view invoice for renewal:', renewal);
                                      console.log('Renewal ID being used:', renewal.id);
                                      console.log('Full renewal object:', JSON.stringify(renewal, null, 2));
                                      
                                      // Open invoice using the API endpoint in a new tab for proper viewing
                                      const invoiceUrl = `/api/historical-renewals/${renewal.id}/invoice`;
                                      console.log('Generated invoice URL:', invoiceUrl);
                                      window.open(invoiceUrl, '_blank');
                                      toast({
                                        title: "Opening invoice",
                                        description: `Opening invoice for renewal dated ${formatDate(renewal.renewalDate)}`,
                                      });
                                    }}
                                  >
                                    <FileTextIcon className="h-3.5 w-3.5 mr-1" />
                                    <span className="underline">View</span>
                                  </Button>
                                ) : (
                                  <span className="text-xs text-slate-400">No invoice</span>
                                )}
                              </div>
                              <div className="flex justify-end space-x-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0"
                                  onClick={() => {
                                    setEditingRenewal(renewal.id);
                                    // In a real app, you'd populate the form with renewal data
                                    toast({
                                      title: "Edit renewal",
                                      description: `Editing renewal dated ${formatDate(renewal.renewalDate)}`,
                                    });
                                  }}
                                >
                                  <Pencil className="h-3.5 w-3.5 text-slate-500" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0"
                                  onClick={() => {
                                    setDeletingRenewal(renewal.id);
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Empty state - shown when no renewals exist */}
                      {false && (
                        <div className="p-6 text-center bg-slate-50">
                          <FileTextIcon className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                          <p className="text-sm text-slate-500">No historical renewal records found.</p>
                          <p className="text-xs text-slate-400 mb-3">Add past renewal records to track your complete license history.</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mx-auto flex items-center"
                            onClick={() => setShowAddHistoricalDialog(true)}
                          >
                            <PlusCircle className="h-3.5 w-3.5 mr-1" />
                            Add Historical Record
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex flex-col sm:flex-row sm:justify-between gap-2 border-t pt-4">
              <Button variant="outline" asChild className="w-full sm:w-auto order-2 sm:order-1">
                <Link href={`/licenses/${licenseId}`}>
                  View Full Details
                </Link>
              </Button>
              
              {/* Add Historical Renewal Dialog */}
              {license && showAddHistoricalDialog && (
                <AddHistoricalRenewalDialog
                  licenseId={license.id}
                  licenseName={license.name}
                  isOpen={showAddHistoricalDialog}
                  onClose={() => setShowAddHistoricalDialog(false)}
                />
              )}
              
              {/* View Invoice Dialog */}
              {license && viewingInvoice && (
                <ViewInvoiceDialog
                  renewalId={viewingInvoice}
                  renewalDate={selectedRenewalDate || new Date()}
                  isOpen={!!viewingInvoice}
                  onClose={() => {
                    setViewingInvoice(null);
                    setSelectedRenewalDate(null);
                  }}
                  updateCounter={invoiceUpdateCounter} // Pass the counter to force rerender
                />
              )}
              
              {/* Edit Renewal Dialog */}
              {license && editingRenewal && (
                <EditHistoricalRenewalDialog
                  renewal={historicalRenewals.find(r => r.id === editingRenewal) ? {
                    id: historicalRenewals.find(r => r.id === editingRenewal)!.id,
                    licenseId: historicalRenewals.find(r => r.id === editingRenewal)!.licenseId,
                    renewalDate: historicalRenewals.find(r => r.id === editingRenewal)!.renewalDate || new Date(),
                    amount: historicalRenewals.find(r => r.id === editingRenewal)!.newCost,
                    notes: historicalRenewals.find(r => r.id === editingRenewal)!.notes,
                    hasInvoice: !!historicalRenewals.find(r => r.id === editingRenewal)!.invoiceUrl
                  } : {
                    id: editingRenewal,
                    licenseId: license.id,
                    renewalDate: new Date(2025, 4, 21),
                    amount: license.cost,
                    notes: "Regular monthly renewal",
                    hasInvoice: true
                  }}
                  isOpen={!!editingRenewal}
                  onClose={() => {
                    setEditingRenewal(null);
                    // Increment counter to force refresh the invoice view
                    setInvoiceUpdateCounter(prev => prev + 1);
                  }}
                />
              )}
              
              {/* Delete Renewal Confirmation Dialog */}
              {license && deletingRenewal && (
                <DeleteRenewalDialog
                  renewalId={deletingRenewal}
                  licenseId={license.id}
                  renewalDate={new Date(2025, 4, 21)}
                  isOpen={!!deletingRenewal}
                  onClose={() => {
                    setDeletingRenewal(null);
                    // Increment counter to force refresh the invoice view
                    setInvoiceUpdateCounter(prev => prev + 1);
                  }}
                />
              )}
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto order-1 sm:order-2 mb-2 sm:mb-0">
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto"
                  onClick={() => {
                    onClose(); // Close the view dialog
                    // Open the edit dialog by dispatching a custom event
                    window.dispatchEvent(new CustomEvent('openEditLicenseDialog', { detail: { licenseId } }));
                  }}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}